import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import { useEffect, useState } from 'react';
import { environment } from 'services/environment/Environment';
import { useStateStorage } from 'store/state';

interface IUseSignalROptions {
  disabled?: boolean;
}

const isAbortError = (error: unknown) => {
  if (!(error instanceof Error)) return false;

  return (
    error.name === 'AbortError' ||
    error.message.includes('stopped during negotiation')
  );
};

export const useSignalR = (hubUrl: string, options?: IUseSignalROptions) => {
  const { token } = useStateStorage();
  const baseURL = environment().api.apiUrl;

  const baseURLSplit = baseURL.split('/');

  const baseURLWithoutVersion = baseURLSplit.slice(0, 4).join('/');

  const [connection, setConnection] = useState<HubConnection | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    if (!token?.length || options?.disabled) {
      setConnection(null);
      setIsConnected(false);
      return;
    }

    let isCancelled = false;

    const newConnection = new HubConnectionBuilder()
      .withUrl(`${baseURLWithoutVersion}/hubs/${hubUrl}`, {
        accessTokenFactory: () => token,
        transport: HttpTransportType.LongPolling,
      })
      .withAutomaticReconnect()
      .build();

    setConnection(newConnection);

    newConnection.onreconnected(() => {
      if (isCancelled) return;
      console.log('SignalR reconnected to ' + hubUrl);
      setIsConnected(true);
    });

    newConnection.onclose(() => {
      if (isCancelled) return;
      console.warn('SignalR disconnected from ' + hubUrl);
      setIsConnected(false);
    });

    const startConnection = async () => {
      try {
        await newConnection.start();

        if (isCancelled) {
          await newConnection.stop();
          return;
        }

        console.log('SignalR Connected to ' + hubUrl);
        setIsConnected(true);
      } catch (err) {
        if (isCancelled || isAbortError(err)) {
          return;
        }

        console.warn('SignalR Connection Error:', err);
        setIsConnected(false);
      }
    };

    startConnection();

    return () => {
      isCancelled = true;
      setIsConnected(false);
      setConnection(null);
      newConnection.stop().catch(() => undefined);
    };
  }, [token, hubUrl, options?.disabled]);

  const send = async <Type>(eventName: string, payload: Type) => {
    if (!connection || connection.state !== HubConnectionState.Connected) {
      console.warn('SignalR not connected yet, skipping event:', eventName);
      return;
    }

    try {
      console.log('Sending via SignalR:', eventName, payload);
      await connection.invoke(eventName, payload);
    } catch (error) {
      console.warn('SignalR Send Error:', error);
    }
  };

  return { connection, isConnected, send };
};
